"use client";

import * as React from "react";
import {
  TransformWrapper,
  TransformComponent,
  type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import useEmblaCarousel from "embla-carousel-react";

import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import ArrowLeftIcon from "@src/lib/icons/18px/arrow-left";
import ArrowRightIcon from "@src/lib/icons/18px/arrow-right";
import { getLinkType, LinkType } from "@src/lib/utils/files";
import VideoPlayer from "@src/lib/ui/VideoPlayer";

export interface MediaGalleryItem {
  url: string;
  type?: "image" | "video" | "pdf";
  poster?: string;
  autoPlay?: boolean;
}

export interface MediaGalleryRenderContext {
  index: number;
  count: number;
  mode: "view" | "zoom";
  canZoom: boolean;
  zoomIn: () => void;
  resetZoom: () => void;
}

type MediaGalleryHeader =
  | React.ReactNode
  | ((ctx: MediaGalleryRenderContext) => React.ReactNode);

export interface MediaGalleryProps {
  items: MediaGalleryItem[];
  initialIndex?: number;
  active?: boolean;
  /** Keep arrow keys from reaching listeners outside a full-screen gallery. */
  isolateKeyboard?: boolean;
  showCounter?: boolean;
  header?: MediaGalleryHeader;
  headerInsetClassName?: string;
  mediaInsetClassName?: string;
  prevButtonClassName?: string;
  nextButtonClassName?: string;
  mediaClassName?: string;
  contentClassName?: string;
  className?: string;
}

// VideoPlayer attaches hls.js, so Mux (.m3u8) streams play in any browser.
// Only the current slide mounts a player: hls.js starts downloading as soon
// as it attaches, and unmounting stops a video when its slide goes away.
function GalleryVideo({
  url,
  poster,
  autoPlay,
  className,
  current,
}: {
  url: string;
  poster?: string;
  autoPlay?: boolean;
  className: string;
  current: boolean;
}) {
  if (!current) return <div className={cn(className, "bg-black")} />;
  return (
    <VideoPlayer
      src={url}
      videoProps={{
        controls: true,
        className,
        preload: "metadata",
        poster,
        autoPlay,
      }}
    />
  );
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i.test(url);
}

function resolveType(item: MediaGalleryItem): "image" | "video" | "pdf" {
  if (item.type) return item.type;
  if (getLinkType(item.url) === LinkType.PDF) return "pdf";
  return isVideoUrl(item.url) ? "video" : "image";
}

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    (el as HTMLElement).isContentEditable
  );
}

// Media overlays sit on a dark stage in both themes, so they use white ink
// over a blurred white tint rather than surface tokens.
const PILL_BASE =
  "pointer-events-auto flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-white/15 px-3.5 text-s-v4 font-medium-v4 text-white backdrop-blur-[12.5px]";

export interface MediaGalleryPillProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  "aria-label"?: string;
}

export function MediaGalleryPill({
  children,
  onClick,
  className,
  "aria-label": ariaLabel,
}: MediaGalleryPillProps): React.ReactElement {
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={cn(
          PILL_BASE,
          "transition-colors outline-none hover:bg-white/25 focus-visible:ring-1 focus-visible:ring-white/30",
          className,
        )}
      >
        {children}
      </button>
    );
  }
  return (
    <div aria-label={ariaLabel} className={cn(PILL_BASE, className)}>
      {children}
    </div>
  );
}

export function MediaGallery({
  items,
  initialIndex = 0,
  active = true,
  isolateKeyboard = false,
  showCounter = false,
  header,
  headerInsetClassName = "inset-x-[15px] top-[15px]",
  mediaInsetClassName = "inset-0",
  prevButtonClassName = "left-[15px]",
  nextButtonClassName = "right-[15px]",
  mediaClassName = "h-full w-full object-contain",
  contentClassName = "flex items-center justify-center",
  className,
}: MediaGalleryProps): React.ReactElement {
  const [mode, setMode] = React.useState<"view" | "zoom">("view");
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const transformRef = React.useRef<ReactZoomPanPinchRef>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    watchDrag: true,
  });

  // Reset state when the gallery becomes active
  React.useEffect(() => {
    if (!active) {
      setMode("view");
      return;
    }
    setCurrentIndex(initialIndex);
  }, [active, initialIndex]);

  // Jump to correct slide when the gallery becomes active
  React.useEffect(() => {
    if (!emblaApi || !active) return;
    emblaApi.scrollTo(initialIndex, true);
  }, [emblaApi, active, initialIndex]);

  // Track current index, reset zoom on slide change
  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
      setMode("view");
      transformRef.current?.resetTransform();
    };
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Toggle drag based on mode
  React.useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ watchDrag: mode === "view" });
  }, [emblaApi, mode]);

  const scrollPrev = React.useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  // Keyboard navigation while active, unless the user is typing elsewhere
  React.useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(document.activeElement)) return;
      const isPrev = event.key === "ArrowLeft" || event.key === "ArrowDown";
      const isNext = event.key === "ArrowRight" || event.key === "ArrowUp";
      if (!isPrev && !isNext) return;
      if (isolateKeyboard) event.stopPropagation();
      if (mode !== "view") return;
      event.preventDefault();
      if (isPrev) scrollPrev();
      else scrollNext();
    };
    document.addEventListener("keydown", onKeyDown, isolateKeyboard);
    return () => {
      document.removeEventListener("keydown", onKeyDown, isolateKeyboard);
    };
  }, [active, isolateKeyboard, mode, scrollPrev, scrollNext]);

  const currentItemType =
    items[currentIndex] !== undefined
      ? resolveType(items[currentIndex])
      : "image";
  const canZoom = currentItemType === "image";

  const zoomIn = React.useCallback(() => {
    setMode("zoom");
    transformRef.current?.zoomIn();
  }, []);

  const resetZoom = React.useCallback(() => {
    setMode("view");
    transformRef.current?.resetTransform();
  }, []);

  const resolvedHeader =
    typeof header === "function"
      ? header({
          index: currentIndex,
          count: items.length,
          mode,
          canZoom,
          zoomIn,
          resetZoom,
        })
      : header;

  return (
    <div className={cn("relative h-full w-full", className)}>
      {/* Carousel */}
      <div
        ref={emblaRef}
        className={cn(
          "absolute",
          mediaInsetClassName,
          mode === "zoom" ? "overflow-visible" : "overflow-hidden",
        )}
      >
        <div className="flex h-full">
          {items.map((item, index) => {
            const type = resolveType(item);
            const isActive = index === currentIndex;

            return (
              <div
                key={index}
                role="group"
                aria-roledescription="slide"
                className="relative flex min-w-0 shrink-0 grow-0 basis-full items-center justify-center"
              >
                {type === "video" ? (
                  <GalleryVideo
                    url={item.url}
                    poster={item.poster}
                    autoPlay={item.autoPlay}
                    className={mediaClassName}
                    current={active && isActive}
                  />
                ) : type === "pdf" ? (
                  <iframe
                    src={`${item.url}#toolbar=0&navpanes=0`}
                    title="PDF"
                    className="h-full w-full border-0 outline-none"
                  />
                ) : (
                  <TransformWrapper
                    ref={isActive ? transformRef : undefined}
                    initialScale={1}
                    minScale={1}
                    maxScale={8}
                    centerOnInit
                    disabled={!isActive || mode === "view"}
                    pinch={{ disabled: true }}
                  >
                    <TransformComponent
                      wrapperClass="!h-full !w-full"
                      contentClass={contentClassName}
                    >
                      <img
                        src={item.url}
                        alt={`${index + 1} of ${items.length}`}
                        className={mediaClassName}
                        draggable={false}
                      />
                    </TransformComponent>
                  </TransformWrapper>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation arrows - only in view mode with multiple items */}
      {mode === "view" && items.length > 1 && (
        <>
          <Button
            variant="blur"
            size="icon"
            className={cn(
              "absolute top-1/2 z-10 -translate-y-1/2 transition-none active:-translate-y-1/2",
              prevButtonClassName,
            )}
            onClick={scrollPrev}
            aria-label="Previous"
          >
            <ArrowLeftIcon />
          </Button>
          <Button
            variant="blur"
            size="icon"
            className={cn(
              "absolute top-1/2 z-10 -translate-y-1/2 transition-none active:-translate-y-1/2",
              nextButtonClassName,
            )}
            onClick={scrollNext}
            aria-label="Next"
          >
            <ArrowRightIcon />
          </Button>
        </>
      )}

      {/* Counter */}
      {showCounter && items.length > 1 && (
        <div className="absolute bottom-[15px] left-1/2 z-10 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 text-s-v4 font-medium-v4 text-white tabular-nums backdrop-blur-[12.5px]">
          {currentIndex + 1}/{items.length}
        </div>
      )}

      {/* Header overlay */}
      {resolvedHeader && (
        <div
          className={cn(
            "pointer-events-none absolute z-10",
            headerInsetClassName,
          )}
        >
          {resolvedHeader}
        </div>
      )}
    </div>
  );
}
